import { z } from 'zod';

export const COMMAND_TYPES = [
  'CLEAR_LOCAL_STORAGE',
  'CHECK_FOR_JS_UPDATE',
  /** Reloads the POS app, applying the latest downloaded EAS update (`Updates.reloadAsync`). */
  'RELOAD_POS',
  'CHECK_FOR_APP_STORE_UPDATE',
  'DISCOVER_LAN_PRINTERS',
  /** Returns `sha256(iPadAuthToken)` (the value stored as `iPads.tokenHash`), creating the token first if needed. */
  'GET_IPAD_TOKEN_HASH',
  /** Returns the screen the terminal is currently on (e.g. `MENU`, `PASSCODE`). */
  'GET_ACTIVE_SCREEN',
] as const;
export const zCommandType = z.literal(COMMAND_TYPES);
export type CommandType = z.output<typeof zCommandType>;

export const COMMAND_STATUSES = ['PENDING', 'EXECUTED', 'FAILED'] as const;
export const zCommandStatus = z.literal(COMMAND_STATUSES);
export type CommandStatus = z.output<typeof zCommandStatus>;

/**
 * `remoteCommands` are only used for POS, though may be extended to other platforms in the future.
 * To schedule a command to run at a later time, use Convex schedulers (rather than adding an `executeAt` field to the schema).
 **/
export const remoteCommandSchema = z.object({
  restaurantId: z.string(),
  /** Since `type`, `payload` and `status` are generic and often reserved keywords, prefix with `command` */
  commandType: zCommandType,
  commandPayload: z.record(z.string(), z.unknown()).nullable().default(null),
  commandStatus: zCommandStatus.default('PENDING'),
  /**
   * `result` is usually `null`, but it can be the error message if `status` is `FAILED`
   * It could also be the response from a command, such as `CHECK_FOR_UPDATES` or `DISCOVER_PRINTERS`.
   */
  commandResult: z.record(z.string(), z.unknown()).nullable().default(null),
  targetPosTerminalUuid: z.string(),

  _creationTime: z.number().optional(),
  _id: z.string().optional(),
});
export type RemoteCommand = z.output<typeof remoteCommandSchema>;

export const remoteCommandStrictSchema = remoteCommandSchema.safeExtend({
  targetPosTerminalUuid: z.guid(),
});

export const remoteCommandCvxSchema = remoteCommandSchema.safeExtend({
  _creationTime: z.number(),
  _id: z.string(),
});
export type RemoteCommandCvx = z.output<typeof remoteCommandCvxSchema>;
